import CustomerReportService from "@/services/api/customerReport";
import type {
  CallLogs,
  CustomerAdded,
  UserFilterPayload,
} from "@/services/api/customerReport";
import { handleResponse } from "@/services/axios";
import { saveAsExcelFile } from "@/services/excelService/excelSevice";
import {
  createAsyncThunk,
  createSlice,
  isFulfilled,
  isRejected,
  isPending,
} from "@reduxjs/toolkit";

interface LeadsByStatusItem {
  Status: string;
  Value: number;
}

interface LeadsByStageItem {
  LeadStage: string;
  Value: number;
}

export interface VisitCalendarRow {
  Rownum: number;
  UserName: string;
  EmpNo: string;
  Manager: string;
  State: string;
  City: string;
  JoinedDate: string;
  [key: string]: any; // dynamic day columns like "1", "2", ... and "1_Status", "2_Status", ...
}

interface CustomerReportState {
  callLogs: CallLogs[];
  customerAdded: CustomerAdded[];
  visitsReport: CustomerAdded[];
  leadsByStatus: LeadsByStatusItem[];
  leadsByStage: LeadsByStageItem[];
  leads: any[];
  visitCalendarData: VisitCalendarRow[];
  visitCalendarTotal: number;
    leadFrequencyVisitReport: any[];
  isFetching: boolean;
  loading: boolean;
  visitCalendarExport: boolean;
  error: string | null;
  coverageReportLoader: boolean;
}

const initialState: CustomerReportState = {
  callLogs: [],
  customerAdded: [],
  visitsReport: [],
  leadsByStatus: [],
  leadsByStage: [],
  leads: [],
    leadFrequencyVisitReport: [],
  loading: false,
  visitCalendarExport: false,
  visitCalendarData: [],
  visitCalendarTotal: 0,
  isFetching: false,
  error: null,
  coverageReportLoader: false,
};

export const getCustomerAdded = createAsyncThunk(
  "customerReport/addedCustomer",
  async (payload: UserFilterPayload, { rejectWithValue }) => {
    try {
      const res = await CustomerReportService.getCustomerAdded(payload);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      rejectWithValue(
        err?.response?.data?.message ||
        "Failed to fetch customers added report",
      );
    }
  },
);

export const exportCustomerAdded = createAsyncThunk(
  "customerReport/exportCustomerAdded",
  async (payload: any, { rejectWithValue }) => {
    try {
      const res = await CustomerReportService.exportCustomerAdded(payload);
      return saveAsExcelFile(res, "CustomerAddedReport");
    } catch (err: any) {
      rejectWithValue(
        err?.response?.data?.message ||
        "Failed to export Customer Added Report",
      );
    }
  },
);

export const getLeadsByStatus = createAsyncThunk(
  "customerReport/leadsByStatus",
  async (payload: any, { rejectWithValue }) => {
    try {
      const res = await CustomerReportService.getLeadsByStatus(payload);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch Leads by Status",
      );
    }
  },
);

export const getLeadsByStage = createAsyncThunk(
  "customerReport/leadsByStage",
  async (payload: any, { rejectWithValue }) => {
    try {
      const res = await CustomerReportService.getLeadsByStage(payload);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || "Failed to fetch Leads by Stage");
    }
  }
);

export const getCallLogs = createAsyncThunk(
  "customerReport/callLogs",
  async (payload: UserFilterPayload, { rejectWithValue }) => {
    try {
      const res = await CustomerReportService.getCallLogs(payload);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      rejectWithValue(
        err?.response?.data?.message || "Failed to fetch call logs report",
      );
    }
  },
);

export const exportCallLogs = createAsyncThunk(
  "customerReport/exportCallLogs",
  async (payload: any, { rejectWithValue }) => {
    try {
      const res = await CustomerReportService.exportCallLogs(payload);
      return saveAsExcelFile(res, "CallLogsReport");
    } catch (err: any) {
      rejectWithValue(
        err?.response?.data?.message || "Failed to export Call Logs Report",
      );
    }
  },
);

export const getVisitReport = createAsyncThunk(
  "customerReport/visitReport",
  async (payload: UserFilterPayload, { rejectWithValue }) => {
    try {
      const res = await CustomerReportService.getVisitReport(payload);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      rejectWithValue(
        err?.response?.data?.message || "Failed to fetch visit report",
      );
    }
  },
);

export const exportVisitReports = createAsyncThunk(
  "customerReport/exportVisitReports",
  async (payload: any, { rejectWithValue }) => {
    try {
      const res = await CustomerReportService.exportVisitReport(payload);
      return saveAsExcelFile(res, "VisitReports");
    } catch (err: any) {
      rejectWithValue(
        err?.response?.data?.message || "Failed to export Visit Reports",
      );
    }
  },
);

export const fetchCustomerCoverageLeads = createAsyncThunk(
  "customerReport/fetchCustomerCoverageLeads",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await CustomerReportService.getCoverageReport(payload);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response.data.message || "Failed");
    }
  },
);

export const getVisitCalendar = createAsyncThunk(
  "customerReport/visitCalendar",
  async (payload: any, { rejectWithValue }) => {
    try {
      const res = await CustomerReportService.getVisitCalendar(payload);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || "Failed to fetch Visit Calendar");
    }
  }
);

export const getVisitCalendarExport = createAsyncThunk(
  "customerReport/getVisitCalendarExport",
  async (payload: any, { rejectWithValue }) => {
    try {
      const res = await CustomerReportService.getVisitCalendarExport(payload);
      return saveAsExcelFile(res, "VISIT_CALENDAR");
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || "Failed to export Visit Calendar");
    }
  }
);

export const getLeadFrequencyVisitReport = createAsyncThunk(
    "customerReport/getLeadFrequencyVisitReport",
    async (payload: any, { rejectWithValue }) => {
        try {
            const response = await CustomerReportService.getLeadFrequencyVisitReport(payload);
            return handleResponse(response, rejectWithValue);
        }
        catch (err: any) {
            return rejectWithValue(
                err?.response.data.message || "Failed"
            );
        }
    },
);

export const exportLeadFrequencyVisitReport = createAsyncThunk(
    "customerReport/exportLeadFrequencyVisitReport",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await CustomerReportService.exportLeadFrequencyVisitReport(payload);
            return saveAsExcelFile(res, "LeadFrequencyVisitReport");
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message ||
                "Failed to export Lead Frequency Visit Report",
            );
        }
    },
);


const customerReportSlice = createSlice({
  name: "customerReport",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // getLeadsByStatus
    builder
      .addCase(getLeadsByStatus.pending, (state) => {
        state.isFetching = true;
        state.error = null;
      })
      .addCase(getLeadsByStatus.fulfilled, (state, action) => {
        state.isFetching = false;
        state.leadsByStatus = action.payload || [];
      })
      .addCase(getLeadsByStatus.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.payload as string || "Failed to fetch Leads by Status";
      });

    // getLeadsByStage
    builder
      .addCase(getLeadsByStage.pending, (state) => {
        state.isFetching = true;
        state.error = null;
      })
      .addCase(getLeadsByStage.fulfilled, (state, action) => {
        state.isFetching = false;
        state.leadsByStage = action.payload || [];
      })
      .addCase(getLeadsByStage.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.payload as string || "Failed to fetch Leads by Stage";
      });

    // getVisitCalendar
    builder
      .addCase(getVisitCalendar.pending, (state) => {
        state.isFetching = true;
        state.error = null;
      })
      .addCase(getVisitCalendar.fulfilled, (state, action) => {
        state.isFetching = false;
        state.visitCalendarData = action.payload?.Table || [];
        state.visitCalendarTotal = action.payload?.TotalRecords || 0;
      })
      .addCase(getVisitCalendar.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.payload as string || "Failed to fetch Visit Calendar";
        state.visitCalendarData = [];
        state.visitCalendarTotal = 0;
      });

    builder
      .addCase(getVisitCalendarExport.pending, (state) => {
        state.visitCalendarExport = true;
        state.error = null;
      })
      .addCase(getVisitCalendarExport.fulfilled, (state) => {
        state.visitCalendarExport = false;
      })
      .addCase(getVisitCalendarExport.rejected, (state, action) => {
        state.visitCalendarExport = false;
        state.error = action.payload as string;
      });

    // getCustomerAdded
    builder
      .addCase(getCustomerAdded.pending, (state) => {
        state.isFetching = true;
        state.error = null;
      })
      .addCase(getCustomerAdded.fulfilled, (state, action) => {
        state.isFetching = false;
        state.customerAdded = action.payload || [];
      })
      .addCase(getCustomerAdded.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.payload as string || "Failed to fetch customers added report";
      });

    // getCallLogs
    builder
      .addCase(getCallLogs.pending, (state) => {
        state.isFetching = true;
        state.error = null;
      })
      .addCase(getCallLogs.fulfilled, (state, action) => {
        state.isFetching = false;
        state.callLogs = action.payload || [];
      })
      .addCase(getCallLogs.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.payload as string || "Failed to fetch call logs report";
      });

    // getVisitReport
    builder
      .addCase(getVisitReport.pending, (state) => {
        state.isFetching = true;
        state.error = null;
      })
      .addCase(getVisitReport.fulfilled, (state, action) => {
        state.isFetching = false;
        state.visitsReport = action.payload || [];
      })
      .addCase(getVisitReport.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.payload as string || "Failed to fetch visit report";
      })
      .addCase(getLeadFrequencyVisitReport.pending, (state) => {
        state.loading = true;
      })
      .addCase(getLeadFrequencyVisitReport.fulfilled, (state, action) => {
        state.loading = false;
        state.leadFrequencyVisitReport = action.payload || [];
      })
      .addCase(getLeadFrequencyVisitReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

    // fetchCustomerCoverageLeads
    builder
      .addCase(fetchCustomerCoverageLeads.pending, (state) => {
        state.coverageReportLoader = true;
        state.leads = [];
        state.error = null;
      })
      .addCase(fetchCustomerCoverageLeads.fulfilled, (state, action) => {
        state.coverageReportLoader = false;
        state.leads = action.payload || [];
      })
      .addCase(fetchCustomerCoverageLeads.rejected, (state, action) => {
        state.coverageReportLoader = false;
        state.error = action.payload as string || "Failed to fetch customer coverage leads";
      });

    // Export thunks (using loading state)
    builder
      .addCase(exportCustomerAdded.pending, (state) => {
        state.loading = true;
      })
      .addCase(exportCustomerAdded.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(exportCustomerAdded.rejected, (state) => {
        state.loading = false;
      })
      .addCase(exportCallLogs.pending, (state) => {
        state.loading = true;
      })
      .addCase(exportCallLogs.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(exportCallLogs.rejected, (state) => {
        state.loading = false;
      })
      .addCase(exportVisitReports.pending, (state) => {
        state.loading = true;
      })
      .addCase(exportVisitReports.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(exportVisitReports.rejected, (state) => {
        state.loading = false;
      });
  },
});

export default customerReportSlice.reducer;
